<template>
  <div
    class="ad-detail-view"
    :class="{
      'ad-detail-view--with-bottom-bar': showBottomActionBar,
      'ad-detail-view--has-draft-banner': showDraftNewerBanner,
      'ad-detail-view--embedded': embedded,
    }"
  >
    <div
      v-if="showDraftNewerBanner"
      class="ad-detail-draft-banner"
      role="status"
    >
      <svg class="ad-detail-draft-banner__icon" width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M4 11H12V9H4V11ZM4 14H12V12H4V14ZM4 17H9V15H4V17ZM2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H10L16 6V18C16 18.55 15.8042 19.0208 15.4125 19.4125C15.0208 19.8042 14.55 20 14 20H2ZM9 7V2H2V18H14V7H9Z" fill="#5D5125"/>
      </svg>
      <div class="ad-detail-draft-banner__content">
        <div class="ad-detail-draft-banner__title">
          {{ $t("lbl_EMPBE_AdDetail_DraftNewer_Title") }}
        </div>
        <p class="ad-detail-draft-banner__desc">
          {{ $t("lbl_EMPBE_AdDetail_DraftNewer_Desc") }}
        </p>
      </div>
    </div>

    <div class="ad-detail-toolbar customStickyCardHeader">
      <div class="ad-detail-toolbar__main">
        <nav class="ad-detail-breadcrumb">
          <button type="button" class="ad-detail-breadcrumb__link" @click="goBack">
            {{ $t("lbl_EMPBE_AdDetail_AllAds") }}
          </button>
          <template v-if="pageTypeLabel">
            <i class="fa fa-angle-right iconRight" aria-hidden="true"></i>
            <span class="ad-detail-breadcrumb__segment">{{ pageTypeLabel }}</span>
            <span class="ad-detail-breadcrumb__dash" aria-hidden="true">-</span>
          </template>
          <i v-else class="fa fa-angle-right iconRight" aria-hidden="true"></i>
          <span class="ad-detail-breadcrumb__current">
            {{ adItemTitle }}<span v-if="breadcrumbDetailIdLabel" class="ad-detail-breadcrumb__id">{{ breadcrumbDetailIdLabel }}</span>
          </span>
          <span
            v-if="!showStatusLine && resolvedStatusKey"
            :class="['ad-detail-toolbar-status', statusTagClass(resolvedStatusKey)]"
          >
            {{ $t(resolveAdStatusI18nKey(resolvedStatusKey, fromAdmin)) }}
          </span>
        </nav>
        <div v-if="showStatusLine" class="ad-detail-status-line">
          <span class="ad-detail-status-label">{{ $t('lbl_EMPBE_ProductsStatus') }}</span>
          <span
            class="ad-detail-status-pill"
            :class="launchStatusClass"
            v-b-tooltip.hover.bottom="viewportTooltip($t('lbl_EMPBE_Ad_LaunchStatus_Tooltip'))"
          >
            <span class="ad-detail-launch-dot" aria-hidden="true"></span>{{ launchStatusLabel }}
          </span>
          <span class="ad-detail-status-label">{{ $t('lbl_EMPBE_ReviewStatus') }}</span>
          <span
            v-if="resolvedStatusKey"
            :class="['ad-detail-status-pill', statusTagClass(resolvedStatusKey)]"
            v-b-tooltip.hover.bottom="viewportTooltip($t('lbl_EMPBE_Ad_ReviewStatus_Tooltip'))"
          >
            {{ $t(resolveAdStatusI18nKey(resolvedStatusKey, fromAdmin)) }}
          </span>
        </div>
      </div>

      <div v-if="showDetailNavButtons && (hasPrev || hasNext)" class="ad-detail-toolbar__actions">
        <div class="ad-detail-nav-group">
          <button
            v-if="hasPrev"
            type="button"
            class="ad-detail-nav-btn"
            :disabled="pageLoading"
            @click="goPrev"
            :t_label="adTLabel('Detail', 'btnPrevious')"
          >
            <i class="el-icon-back ad-detail-nav-btn__icon" aria-hidden="true"></i>
            {{ $t("lbl_EMPBE_AdDetail_PreviousItem") }}
          </button>
          <button
            v-if="hasNext"
            type="button"
            class="ad-detail-nav-btn"
            :disabled="pageLoading"
            @click="goNext"
            :t_label="adTLabel('Detail', 'btnNext')"
          >
            {{ $t("lbl_EMPBE_AdDetail_NextItem") }}
            <i class="el-icon-right ad-detail-nav-btn__icon" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showSupplierRejectBanner"
      class="ad-detail-reject-banner"
    >
      <div class="ad-detail-reject-banner__head">
        <span class="ad-detail-reject-banner__title">
          {{ $t("lbl_EMPBE_advertisingInfo_RejectReasonTitle") }}
        </span>
      </div>
      <p class="ad-detail-reject-banner__reason">{{ rejectReasonText || "—" }}</p>
    </div>

    <div
      v-if="showPriorYearReuseBanner"
      class="ad-prior-year-reuse-banner"
    >
      <div class="ad-prior-year-reuse-banner__content">
        <div class="ad-prior-year-reuse-banner__title-row">
          <i class="fa fa-magic ad-prior-year-reuse-banner__icon" aria-hidden="true"></i>
          <span class="ad-prior-year-reuse-banner__title">
            {{ $t("lbl_EMPBE_AdDetail_ReusePriorYear_Title") }}
          </span>
        </div>
        <p class="ad-prior-year-reuse-banner__desc">
          {{ $t("lbl_EMPBE_AdDetail_ReusePriorYear_Desc") }}
        </p>
      </div>
      <div class="ad-prior-year-reuse-banner__actions">
        <button
          type="button"
          class="ad-prior-year-reuse-banner__link"
          :disabled="priorPreviewLoading"
          @click="onPreviewPriorYear"
          :t_label="adTLabel('Detail', 'btnPreviewPriorYear')"
        >
          {{ $t("lbl_EMPBE_AdDetail_ReusePriorYear_Preview") }}
        </button>
      </div>
    </div>

    <b-overlay
      :show="pageLoading"
      rounded
      opacity="0.6"
      spinner-small
      class="ad-detail-overlay"
      :class="{ 'ad-detail-overlay--compact': isCompactDetailLayout }"
      :style="loadingShellStyle"
    >
    <div
      ref="detailShell"
      class="ad-detail-card"
      :class="{
        'ad-detail-card--loading': pageLoading,
        'ad-detail-card--compact': isCompactDetailLayout,
      }"
    >
      <div class="ad-detail-card__body">
      <AdInfoCenterCore
        ref="core"
        :key="detailCoreKey"
        detail-layout
        :companyId="resolvedCompanyId"
        :detail-ad-detail-id="resolvedAdDetailId"
        :detail-ad-package-id="resolvedAdPackageId"
        :detail-pc-ad-detail-id="resolvedPcAdDetailId"
        :detail-pc-ad-package-id="resolvedPcAdPackageId"
        :detail-mobile-ad-detail-id="resolvedMobileAdDetailId"
        :detail-mobile-ad-package-id="resolvedMobileAdPackageId"
        :detail-page-type="resolvedPageType"
        :detail-admin-display-status-hint="listRow && listRow.adminDisplayStatus"
        :fromAdmin="fromAdmin"
        @core-ready="onCoreReady"
        @detail-meta-loaded="onDetailMetaLoaded"
        @detail-loading="onDetailLoading"
      >
        <template #detail-header>
          <div v-if="detailMeta" class="ad-detail-card-header">
            <div class="ad-detail-card-header__top">
              <span class="ad-detail-card-header__title">{{ $t("lbl_EMPBE_AdDetail_Configuration") }}</span>
            </div>
            <div class="ad-detail-display-in">
              <div class="ad-detail-display-in__label">{{ $t("lbl_EMPBE_AdDetail_DisplayIn") }}</div>
              <div class="ad-detail-platform-row">
                <div class="ad-detail-platform-main">
                  <span class="ad-detail-platform-logo">
                    <img
                      :src="platformLogoUrl(detailMeta.displaySection)"
                      :alt="platformLabel"
                      @error="onPlatformLogoError($event)"
                    />
                  </span>
                  <span class="ad-detail-platform-name">{{ platformLabel }}</span>
                </div>
                <span
                  v-if="showOpenDisplay"
                  class="ad-detail-open-display"
                  tabindex="0"
                  aria-describedby="tips-open-display"
                >
                  <AdDisplayPreviewButton
                    :url="displayPreviewUrl"
                    variant="link"
                    :t-label="adTLabel('Detail', 'btnOpenDisplay')"
                  />
                  <div
                    id="tips-open-display"
                    class="tips-pop"
                    role="tooltip"
                    aria-live="polite"
                  >
                    <div class="tips-box">
                      <i class="tips-arrow" aria-hidden="true"></i>
                      <h4 class="tips-title">{{ $t("lbl_EMPBE_AdDetail_OpenDisplayTip") }}</h4>
                    </div>
                  </div>
                </span>
              </div>
            </div>
          </div>

        </template>
      </AdInfoCenterCore>
      </div>
    </div>
    </b-overlay>

    <div v-if="showBottomActionBar" class="ad-detail-bottom-bar">
      <button
        v-if="showSaveButton"
        type="button"
        class="ad-detail-bottom-bar__save"
        :disabled="!hasCoreAd || pageLoading"
        @click="onSave"
        :t_label="adTLabel('Detail', 'btnSave')"
      >
        <i class="fa fa-save" aria-hidden="true"></i>
        {{ $t("lbl_EMPBE_CompanyInfo_Save") }}
      </button>
      <button
        type="button"
        class="ad-detail-bottom-bar__submit"
        :disabled="!hasCoreAd || pageLoading"
        @click="onSubmit"
        :t_label="adTLabel('Detail', 'btnSubmit')"
      >
        <i class="fa fa-send" aria-hidden="true"></i>
        {{ $t("lbl_EMPBE_CompanyInfo_Submit") }}
      </button>
      <button
        v-if="IsUAT"
        type="button"
        class="ad-detail-bottom-bar__sync"
        @click="btnSyncAdToFrontEnd_Click"
        :t_label="adTLabel('Detail', 'btnSyncToFrontEnd')"
      >
        <i class="fa fa-refresh" aria-hidden="true"></i>
        {{ $t("Lbl_AdSyncToFrontEnd") }} (Debug)
      </button>
    </div>
  </div>
</template>

<script>
import { AlertDialog } from "@/_helpers/AlertDialog";
import AdInfoCenterCore from "./AdInfoCenterCore.vue";
import AdDisplayPreviewButton from "./AdDisplayPreviewButton.vue";
import { adService } from "../../_services";
import { viewportTooltip } from "@/_helpers/tooltip";
import {
  AD_ROW_STATUS,
  AD_ROW_STATUS_I18N,
  resolveAdStatusKey,
  resolveAdStatusI18nKey,
  DISPLAY_IN_I18N,
  getSubTypeMeta,
  getAdPlatformLogoUrl,
  PAGE_TYPE_I18N,
  canShowAdSaveButton,
  pageTypeHasImageUpload,
  applyDisplayPreviewLangPath,
  buildAdInfoCenterTLabel,
  resolveAdInfoCenterTrackingRoleId,
} from "./adDisplayConfig";
import { getAdDetailNavContext, syncAdDetailNavContext } from "./adDetailNavContext";

const AD_DETAIL_SHELL_MIN_HEIGHT = 640;

export default {
  name: "AdDetailView",
  mixins: [AlertDialog],
  components: {
    AdInfoCenterCore,
    AdDisplayPreviewButton,
  },
  props: {
    companyId: { type: [String, Number], default: null },
    adDetailId: { type: [String, Number], default: null },
    adPackageId: { type: [String, Number], default: null },
    pcAdDetailId: { type: [String, Number], default: null },
    pcAdPackageId: { type: [String, Number], default: null },
    mobileAdDetailId: { type: [String, Number], default: null },
    mobileAdPackageId: { type: [String, Number], default: null },
    pageType: { type: String, default: null },
    listRow: { type: Object, default: null },
    fromAdmin: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      AD_ROW_STATUS_I18N,
      detailMeta: null,
      navContext: null,
      navLoading: false,
      coreLoading: false,
      hasCoreAd: false,
      priorYearReuseInfo: null,
      priorPreviewLoading: false,
      syncAdSkipApproval: false,
    };
  },
  computed: {
    showBottomActionBar() {
      return !this.fromAdmin && !this.embedded && !this.isDetailExpired;
    },
    rejectReasonText() {
      return adService.resolveAdRejectReasonText(this.detailMeta?.rejectReason || '');
    },
    /** 管理员 / 供应商详情：展示状态(上/下架) + 审核状态 */
    showStatusLine() {
      return true;
    },
    /** Ad_LaunchStatus 无行 / 未回传 → Offline（不做审批态兜底上架） */
    isLaunchOnline() {
      const raw = this.detailMeta?.isLaunch ?? this.listRow?.isLaunch;
      return raw === 1 || raw === true || raw === '1';
    },
    launchStatusLabel() {
      return this.isLaunchOnline
        ? this.$t('lbl_EMPBE_Product_Online')
        : this.$t('lbl_EMPBE_Product_Offline');
    },
    launchStatusClass() {
      return this.isLaunchOnline ? 'is-launch-online' : 'is-launch-offline';
    },
    /** 供应商驳回横幅：已驳回时始终展示（不受 SHOW_EDIT_SUPPLIER_REJECT_REASON 影响） */
    showSupplierRejectBanner() {
      if (this.fromAdmin) return false;
      return !!(this.detailMeta && this.detailMeta.displayStatus === 'rejected');
    },
    currentDisplayStatus() {
      return this.detailMeta?.displayStatus
        ?? this.listRow?.displayStatus
        ?? this.routeDetailContext?.displayStatus
        ?? null;
    },
    resolvedStatusKey() {
      if (this.fromAdmin) {
        const adminStatus = this.detailMeta?.adminDisplayStatus
          || this.listRow?.adminDisplayStatus;
        if (adminStatus) return adminStatus;
      }
      return resolveAdStatusKey(this.detailMeta || this.listRow, this.fromAdmin);
    },
    showSaveButton() {
      return this.showBottomActionBar && canShowAdSaveButton(this.currentDisplayStatus);
    },
    resolvedCompanyId() {
      return this.companyId ?? this.$store.state.authentication.user.empCompanyId;
    },
    trackingRoleId() {
      return resolveAdInfoCenterTrackingRoleId(this.$store);
    },
    langid() {
      return localStorage.getItem("currentLangId");
    },
    IsUAT() {
      return process.env.VUE_APP_EMP_IsUat === 'true' || process.env.VUE_APP_EMP_IsUat === '1';
    },
    resolvedAdDetailId() {
      return this.adDetailId
        ?? this.routeDetailContext?.adDetailId
        ?? this.$route.params.adDetailId
        ?? null;
    },
    routeDetailContext() {
      return adService.parseAdDetailRouteContext(this.$route);
    },
    resolvedPcAdDetailId() {
      return this.pcAdDetailId
        ?? this.routeDetailContext?.pcAdDetailId
        ?? this.listRow?.pcAdDetailId
        ?? null;
    },
    resolvedMobileAdDetailId() {
      return this.mobileAdDetailId
        ?? this.routeDetailContext?.mobileAdDetailId
        ?? this.listRow?.mobileAdDetailId
        ?? null;
    },
    resolvedAdPackageId() {
      return this.adPackageId
        ?? this.routeDetailContext?.adPackageId
        ?? this.listRow?.adPackageId
        ?? null;
    },
    resolvedPcAdPackageId() {
      return this.pcAdPackageId
        ?? this.routeDetailContext?.pcAdPackageId
        ?? this.listRow?.pcAdPackageId
        ?? null;
    },
    resolvedMobileAdPackageId() {
      return this.mobileAdPackageId
        ?? this.routeDetailContext?.mobileAdPackageId
        ?? this.listRow?.mobileAdPackageId
        ?? null;
    },
    resolvedPageType() {
      return this.pageType
        ?? this.routeDetailContext?.pageType
        ?? this.listRow?.pageType
        ?? this.detailMeta?.pageType
        ?? null;
    },
    pageTypeLabel() {
      const pageType = this.resolvedPageType;
      if (!pageType || !PAGE_TYPE_I18N[pageType]) return "";
      return this.$t(PAGE_TYPE_I18N[pageType]);
    },
    showDetailIdHint() {
      return this.resolvedPcAdDetailId && this.resolvedMobileAdDetailId
        && Number(this.resolvedPcAdDetailId) !== Number(this.resolvedMobileAdDetailId);
    },
    breadcrumbDetailIdLabel() {
      if (this.showDetailIdHint) {
        return ` #${this.resolvedPcAdDetailId}, #${this.resolvedMobileAdDetailId}`;
      }
      const id = this.resolvedAdDetailId;
      return id ? ` #${id}` : "";
    },
    adItemTitle() {
      if (this.detailMeta?.titleKey) return this.$t(this.detailMeta.titleKey);
      const ctx = this.$refs.core?.detailContext;
      if (ctx?.group?.subType) return this.$t(getSubTypeMeta(ctx.group.subType).titleKey);
      return "—";
    },
    platformLabel() {
      if (!this.detailMeta?.displayInKey) return "—";
      return this.$t(this.detailMeta.displayInKey);
    },
    showOpenDisplay() {
      return !!(this.detailMeta?.displayPreviewUrl);
    },
    displayPreviewUrl() {
      return applyDisplayPreviewLangPath(this.detailMeta?.displayPreviewUrl, this.langid);
    },
    hasPrev() {
      return !!(this.navContext && this.navContext.globalIndex > 0);
    },
    hasNext() {
      if (!this.navContext) return false;
      return this.navContext.globalIndex < this.navContext.totalCount - 1;
    },
    showDetailNavButtons() {
      return !this.fromAdmin || this.embedded;
    },
    isDetailExpired() {
      const status = this.detailMeta?.displayStatus
        ?? this.listRow?.displayStatus
        ?? this.routeDetailContext?.displayStatus;
      return status === AD_ROW_STATUS.expired;
    },
    showDraftNewerBanner() {
      if (this.fromAdmin || this.isDetailExpired) return false;
      if (this.currentDisplayStatus === AD_ROW_STATUS.toBeSubmitted) return false;
      const saveRaw = this.detailMeta?.lastSaveDate
        ?? this.listRow?.lastSaveDate
        ?? null;
      const submitRaw = this.detailMeta?.lastSubmitDate
        ?? this.listRow?.lastSubmitDate
        ?? null;
      const saveMs = this.parseAdDateMs(saveRaw);
      const submitMs = this.parseAdDateMs(submitRaw);
      if (saveMs == null || submitMs == null) return false;
      return saveMs > submitMs;
    },
    pageLoading() {
      return this.navLoading || this.coreLoading;
    },
    isCompactDetailLayout() {
      return this.resolvedPageType && this.resolvedPageType !== 'bannerAd';
    },
    loadingShellStyle() {
      if (!this.pageLoading) return null;
      return { minHeight: `${AD_DETAIL_SHELL_MIN_HEIGHT}px` };
    },
    detailCoreKey() {
      return [
        this.resolvedAdDetailId,
        this.resolvedPcAdDetailId,
        this.resolvedMobileAdDetailId,
        this.resolvedPageType,
      ].filter((v) => v != null && v !== '').join('_');
    },
    showPriorYearReuseBanner() {
      if (this.fromAdmin) return false;
      if (!pageTypeHasImageUpload(this.resolvedPageType)) return false;
      if (this.currentDisplayStatus !== AD_ROW_STATUS.toBeSubmitted) return false;
      return !!(this.priorYearReuseInfo && this.priorYearReuseInfo.canReuse);
    },
    detailApiPayload() {
      return {
        adDetailId: this.resolvedAdDetailId,
        adPackageId: this.resolvedAdPackageId,
        pcAdDetailId: this.resolvedPcAdDetailId,
        pcAdPackageId: this.resolvedPcAdPackageId,
        mobileAdDetailId: this.resolvedMobileAdDetailId,
        mobileAdPackageId: this.resolvedMobileAdPackageId,
        pageType: this.resolvedPageType,
      };
    },
  },
  watch: {
    listRow: {
      immediate: true,
      handler(row) {
        if (row) {
          this.detailMeta = this.enrichDetailMeta(row);
          this.$emit("adTabChange", this.statusCodeFromMeta(row), this.isLaunchOnline);
        }
      },
    },
    routeDetailContext: {
      immediate: true,
      handler(ctx) {
        if (this.listRow || !ctx) return;
        if (ctx.titleKey || ctx.displayInKey || ctx.displayStatus) {
          this.detailMeta = this.enrichDetailMeta(ctx);
          this.$emit("adTabChange", this.statusCodeFromMeta(ctx), this.isLaunchOnline);
        }
      },
    },
    resolvedAdDetailId: {
      immediate: true,
      handler() {
        this.syncNavIndexFromRoute();
        this.resetPriorYearReuseState();
        this.loadPriorYearReuseInfo();
      },
    },
    currentDisplayStatus() {
      this.loadPriorYearReuseInfo();
    },
    resolvedPageType() {
      this.loadPriorYearReuseInfo();
    },
    resolvedCompanyId() {
      this.loadNavContext();
    },
  },
  mounted() {
    this.loadNavContext();
    this.normalizeDetailRoute();
  },
  methods: {
    viewportTooltip,
    resolveAdStatusI18nKey,
    parseAdDateMs(value) {
      if (value == null || value === '' || value === '-') return null;
      const raw = String(value).trim();
      if (!raw || raw.startsWith('0001-01-01') || raw.startsWith('1900-01-01')) return null;
      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const ms = Date.parse(normalized);
      return Number.isNaN(ms) ? null : ms;
    },
    adTLabel(location, action, subType = '') {
      const actionKey = subType ? `${subType}_${action}` : action;
      return buildAdInfoCenterTLabel(
        location,
        actionKey,
        this.resolvedCompanyId,
        this.trackingRoleId,
        this.resolvedAdDetailId
      );
    },
    showPriorYearReuseFailedPopup() {
      if (!this.$swal) return;
      this.$swal({
        text: this.$t("lbl_EMPBE_AdDetail_ReusePriorYear_Failed"),
        icon: "error",
        showCancelButton: false,
      });
    },
    normalizeDetailRoute() {
      if (this.embedded || this.$route.name !== "AdvertismentInfoCenterDetailById") return;
      if (!this.$route.query.ctx) return;
      const ctx = this.routeDetailContext;
      if (!ctx?.adDetailId) return;
      if (String(ctx.adDetailId) !== String(this.$route.params.adDetailId)) return;
      this.$router.replace(adService.buildAdDetailRouteLocation(ctx));
    },
    onCoreReady(hasAd) {
      this.hasCoreAd = !!hasAd;
    },
    onDetailMetaLoaded(meta) {
      if (!meta) return;
      const merged = this.enrichDetailMeta(meta);
      if (this.fromAdmin && !merged.adminDisplayStatus) {
        merged.adminDisplayStatus = this.listRow?.adminDisplayStatus || null;
      }
      this.detailMeta = merged;
      this.$emit("adTabChange", this.statusCodeFromMeta(this.detailMeta), this.isLaunchOnline);
      this.loadPriorYearReuseInfo();
    },
    onDetailLoading(loading) {
      this.coreLoading = !!loading;
    },
    onSave() {
      this.$refs.core?.btnSave_Click?.();
    },
    onSubmit() {
      this.$refs.core?.btnSubmit_Click?.();
    },
    btnSyncAdToFrontEnd_Click() {
      // Collect all available adDetailIds from this detail view
      const adDetailIds = new Set();
      [this.resolvedAdDetailId, this.resolvedPcAdDetailId, this.resolvedMobileAdDetailId]
        .forEach(id => {
          if (id != null && Number(id) > 0) {
            adDetailIds.add(Number(id));
          }
        });

      if (adDetailIds.size === 0) {
        this.$swal({
          text: this.$t("Lbl_AdSyncToFrontEnd_Failed"),
          confirmButtonText: this.$t("lbl_EMPBE_ReportAll_component_Import_OK"),
        });
        return;
      }

      const delimitedAdDetailId = Array.from(adDetailIds).join(',');

      this.$swal({
        title: this.$t("lbl_EMPBE_AreYouSureToSyncAdToFrontEnd"),
        html: `
          <div style="text-align:left; margin-bottom:12px;">
            <div><strong>adDetailId:</strong> ${delimitedAdDetailId}</div>
          </div>
          <label style="display:flex; align-items:center; gap:8px; cursor:pointer;">
            <input type="checkbox" id="swal-syncAdSkipApproval" />
            Skip Ad Approval
          </label>
        `,
        showCancelButton: true,
        confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
        cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
        didOpen: () => {
          const checkbox = document.getElementById('swal-syncAdSkipApproval');
          if (checkbox) checkbox.checked = this.syncAdSkipApproval;
        },
        preConfirm: () => {
          const checkbox = document.getElementById('swal-syncAdSkipApproval');
          this.syncAdSkipApproval = checkbox ? checkbox.checked : false;
        },
      }).then(async (res) => {
        if (res.isConfirmed) {
          adService.SyncAdToFrontEnd({
            delimitedAdDetailId: delimitedAdDetailId,
            skipAdApproval: this.syncAdSkipApproval ? 1 : 0
          }).then(result => {
            if (result.isSuccessful) {
              this.$swal({
                text: this.$t("Lbl_AdSyncToFrontEnd_Success"),
                confirmButtonText: this.$t("lbl_EMPBE_ReportAll_component_Import_OK"),
              });
            } else {
              const msg = result.msg && result.msg.length ? result.msg.join('; ') : this.$t("Lbl_AdSyncToFrontEnd_Failed");
              this.$swal({
                text: msg,
                confirmButtonText: this.$t("lbl_EMPBE_ReportAll_component_Import_OK"),
              });
            }
          }).catch(err => {
            console.error('SyncAdToFrontEnd error:', err);
            this.$swal({
              text: this.$t("Lbl_AdSyncToFrontEnd_Failed"),
              confirmButtonText: this.$t("lbl_EMPBE_ReportAll_component_Import_OK"),
            });
          });
        }
      });
    },
    enrichDetailMeta(row) {
      const displaySection = row.displayInKey === DISPLAY_IN_I18N.cps ? "cps" : "emp";
      return { ...row, displaySection };
    },
    statusTagClass(status) {
      const map = {
        live: "ad-status-tag ad-status-tag--live",
        pendingGoLive: "ad-status-tag ad-status-tag--pending-go-live",
        pending: "ad-status-tag ad-status-tag--pending",
        aiReviewing: "ad-status-tag ad-status-tag--ai-reviewing",
        pendingManualAudit: "ad-status-tag ad-status-tag--pending-manual",
        rejected: "ad-status-tag ad-status-tag--rejected",
        toBeSubmitted: "ad-status-tag ad-status-tag--draft",
        expired: "ad-status-tag ad-status-tag--expired",
      };
      return map[status] || "ad-status-tag ad-status-tag--draft";
    },
    platformLogoUrl(section) {
      return getAdPlatformLogoUrl(section);
    },
    onPlatformLogoError(event) {
      if (event?.target) event.target.style.visibility = "hidden";
    },
    loadNavContext() {
      const ctx = getAdDetailNavContext(this.resolvedCompanyId);
      if (ctx && typeof ctx.totalCount === "number" && Array.isArray(ctx.items)) {
        this.navContext = ctx;
        this.syncNavIndexFromRoute();
      } else {
        this.navContext = null;
      }
    },
    persistNavContext() {
      syncAdDetailNavContext(this.resolvedCompanyId);
    },
    syncNavIndexFromRoute() {
      if (!this.navContext?.items?.length) return;
      const idx = this.navContext.items.findIndex((row) => this.rowMatchesDetailIds(row));
      if (idx >= 0) {
        this.navContext.indexInPage = idx;
        this.navContext.globalIndex = (this.navContext.pageIndex - 1) * this.navContext.pageSize + idx;
        this.persistNavContext();
      }
    },
    async fetchListPage(pageIndex) {
      const ctx = this.navContext;
      if (!ctx) return [];
      const res = await adService.GetAdRecordList(
        this.resolvedCompanyId,
        this.langid,
        this.fromAdmin,
        {
          pageIndex,
          pageSize: ctx.pageSize,
          ...ctx.serverQuery,
        }
      );
      return res.isSuccessful ? (res.data?.items || []) : [];
    },
    async confirmLeaveIfDirty() {
      const core = this.$refs.core;
      if (!core?.dirty) return true;
      const result = await this.showLeavePage();
      if (result.isConfirmed) {
        core.clearDirty();
        return true;
      }
      return false;
    },
    async goPrev() {
      if (!this.hasPrev || this.pageLoading) return;
      if (!(await this.confirmLeaveIfDirty())) return;
      const ctx = this.navContext;
      if (!ctx) return;

      if (ctx.indexInPage > 0) {
        const prevRow = ctx.items[ctx.indexInPage - 1];
        ctx.indexInPage -= 1;
        ctx.globalIndex -= 1;
        this.persistNavContext();
        this.navigateToRow(prevRow);
        return;
      }

      const prevPageIndex = ctx.pageIndex - 1;
      if (prevPageIndex < 1) return;

      this.navLoading = true;
      try {
        const items = await this.fetchListPage(prevPageIndex);
        if (!items.length) return;
        ctx.pageIndex = prevPageIndex;
        ctx.items = items.map((row) => adService.toAdDetailNavRow(row));
        ctx.indexInPage = ctx.items.length - 1;
        ctx.globalIndex -= 1;
        this.persistNavContext();
        this.navigateToRow(ctx.items[ctx.indexInPage]);
      } finally {
        this.navLoading = false;
      }
    },
    async goNext() {
      if (!this.hasNext || this.pageLoading) return;
      if (!(await this.confirmLeaveIfDirty())) return;
      const ctx = this.navContext;
      if (!ctx) return;

      if (ctx.indexInPage < ctx.items.length - 1) {
        const nextRow = ctx.items[ctx.indexInPage + 1];
        ctx.indexInPage += 1;
        ctx.globalIndex += 1;
        this.persistNavContext();
        this.navigateToRow(nextRow);
        return;
      }

      const nextPageIndex = ctx.pageIndex + 1;
      this.navLoading = true;
      try {
        const items = await this.fetchListPage(nextPageIndex);
        if (!items.length) return;
        ctx.pageIndex = nextPageIndex;
        ctx.items = items.map((row) => adService.toAdDetailNavRow(row));
        ctx.indexInPage = 0;
        ctx.globalIndex += 1;
        this.persistNavContext();
        this.navigateToRow(ctx.items[0]);
      } finally {
        this.navLoading = false;
      }
    },
    navigateToRow(row) {
      if (!row) return;
      if (row.titleKey || row.displayInKey) {
        this.detailMeta = this.enrichDetailMeta(row);
        this.$emit("adTabChange", this.statusCodeFromMeta(this.detailMeta), this.isLaunchOnline);
      }
      if (this.embedded) {
        this.coreLoading = true;
        this.$emit("open-detail", {
          row,
          adDetailId: row.adDetailId,
          adPackageId: row.adPackageId ?? null,
          pageType: row.pageType,
          pcAdDetailId: row.pcAdDetailId ?? null,
          pcAdPackageId: row.pcAdPackageId ?? null,
          mobileAdDetailId: row.mobileAdDetailId ?? null,
          mobileAdPackageId: row.mobileAdPackageId ?? null,
        });
        return;
      }
      this.coreLoading = true;
      this.$router.push(adService.buildAdDetailRouteLocation(row));
    },
    todoSave() {
      return this.$refs.core?.todoSave();
    },
    refresh() {
      return this.$refs.core?.refresh();
    },
    getAdDetails() {
      return this.$refs.core?.getAdDetails() || [];
    },
    prepareAdApproval(intent) {
      return this.$refs.core?.prepareAdApproval?.(intent) || Promise.resolve({ ok: false });
    },
    statusCodeFromMeta(meta) {
      if (!meta) return "";
      if (this.fromAdmin && meta.adminDisplayStatus) {
        return meta.adminDisplayStatus;
      }
      const map = {
        live: "A",
        pendingGoLive: "A",
        pending: "P",
        pendingManualAudit: "PA",
        rejected: "R",
      };
      return map[meta.displayStatus] || meta.statusCode || "";
    },
    rowMatchesDetailIds(row) {
      const ids = [
        this.resolvedAdDetailId,
        this.resolvedPcAdDetailId,
        this.resolvedMobileAdDetailId,
      ].filter(Boolean).map(String);
      return ids.some((id) =>
        [row.adDetailId, row.pcAdDetailId, row.mobileAdDetailId]
          .filter(Boolean)
          .map(String)
          .includes(id)
      );
    },
    goBack() {
      if (this.embedded) {
        this.$emit("close-detail");
        return;
      }
      this.$router.push({ name: "AdvertismentInfoCenter" });
    },
    resetPriorYearReuseState() {
      this.priorYearReuseInfo = null;
    },
    async loadPriorYearReuseInfo() {
      if (this.fromAdmin || !this.resolvedAdDetailId) return;
      if (!pageTypeHasImageUpload(this.resolvedPageType)) {
        this.priorYearReuseInfo = null;
        return;
      }
      const status = this.currentDisplayStatus;
      if (status && status !== AD_ROW_STATUS.toBeSubmitted) {
        this.priorYearReuseInfo = null;
        return;
      }
      try {
        const res = await adService.GetAdPriorYearReuseInfo(
          this.resolvedCompanyId,
          this.langid,
          this.detailApiPayload
        );
        if (res?.isSuccessful && res.data?.canReuse) {
          this.priorYearReuseInfo = res.data;
        } else {
          this.priorYearReuseInfo = null;
        }
      } catch (e) {
        this.priorYearReuseInfo = null;
      }
    },
    buildPriorDetailPayload() {
      const info = this.priorYearReuseInfo;
      if (!info) return null;
      const priorAdDetailId = Number(info.priorPcAdDetailId) || Number(info.priorMobileAdDetailId) || 0;
      if (!priorAdDetailId) return null;
      return {
        adDetailId: priorAdDetailId,
        adPackageId: info.priorPcAdPackageId || info.priorMobileAdPackageId,
        pcAdDetailId: info.priorPcAdDetailId,
        pcAdPackageId: info.priorPcAdPackageId,
        mobileAdDetailId: info.priorMobileAdDetailId,
        mobileAdPackageId: info.priorMobileAdPackageId,
        pageType: this.resolvedPageType,
        includeExpired: true,
      };
    },
    async fetchPriorYearDetailResult() {
      const payload = this.buildPriorDetailPayload();
      if (!payload) return null;
      const res = await adService.GetAdRecordDetail(
        this.resolvedCompanyId,
        this.langid,
        payload
      );
      return res?.isSuccessful ? res.data : null;
    },
    async onPreviewPriorYear() {
      if (this.priorPreviewLoading) return;
      this.priorPreviewLoading = true;
      try {
        const data = await this.fetchPriorYearDetailResult();
        if (!data) {
          this.showPriorYearReuseFailedPopup();
          return;
        }
        if (!this.$refs.core?.applyPriorYearData?.(data)) {
          this.showPriorYearReuseFailedPopup();
        }
      } catch (e) {
        console.error("Preview prior year failed:", e);
        this.showPriorYearReuseFailedPopup();
      } finally {
        this.priorPreviewLoading = false;
      }
    },
    formatDetailDateTime(raw) {
      if (!raw || raw === "-" || String(raw).startsWith("0001-01-01")) return "—";
      // 详情页时间只展示到分钟，不展示秒
      return String(raw).trim().replace("T", " ").replace(/\.\d+$/, "").replace(/:\d{2}$/, "");
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.embedded) {
      next();
      return;
    }
    this.confirmLeaveIfDirty().then((ok) => {
      if (ok) next();
    });
  },
};
</script>

<style lang="scss" scoped>
.ad-detail-view {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-bottom: 32px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  align-self: stretch;

  > * + * {
    margin-top: 16px;
  }

  &--with-bottom-bar {
    padding-bottom: 88px;
  }

  /* 管理员嵌入详情：白底卡片撑满可用显示区域 */
  &--embedded {
    flex: 1 1 auto;
    min-height: calc(100vh - 260px);
    height: 100%;
  }

  /* 横向滚动只发生在内容区，避免吸顶标题被 overflow 截断 */
  &:has(.adPageCard--logo-wide) {
    overflow-x: visible;
  }

  &:has(.adPageCard--logo-wide) .ad-detail-card {
    width: max-content;
    min-width: 100%;
    max-width: none;
  }

  &:has(.adPageCard--logo-wide) .ad-detail-overlay {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    display: block;
  }

  &:has(.adPageCard--logo-wide) .ad-detail-overlay ::v-deep > .b-overlay-wrap {
    width: max-content !important;
    min-width: 100% !important;
    max-width: none !important;
    position: relative !important;
  }

  &:has(.adPageCard--logo-wide) .ad-detail-card__body {
    overflow: visible;
    max-width: none;
    width: max-content;
    min-width: 100%;
  }

  &:has(.adPageCard--logo-wide) ::v-deep .ad-detail-mode-body,
  &:has(.adPageCard--logo-wide) ::v-deep .ad-record-group-panel,
  &:has(.adPageCard--logo-wide) ::v-deep .ad-info-center-core {
    width: max-content;
    min-width: 100%;
    max-width: none;
  }

  &--has-draft-banner:not(.ad-detail-view--embedded) {
    margin-top: -32px;
  }

  /* 仅草稿条后的第一个兄弟补间距；勿波及驳回横幅等后续块（否则叠吸顶 padding 显得过高） */
  &--has-draft-banner:not(.ad-detail-view--embedded) > .ad-detail-draft-banner + * {
    margin-top: 16px;
  }

  &--has-draft-banner:not(.ad-detail-view--embedded) .ad-detail-draft-banner {
    width: calc(100% + 96px);
    max-width: calc(100% + 96px);
    margin-left: -32px;
    margin-right: -48px;
  }
}

@media screen and (max-width: 991.98px) {
  .ad-detail-view--has-draft-banner:not(.ad-detail-view--embedded) {
    margin-top: -12px;
  }

  .ad-detail-view--has-draft-banner:not(.ad-detail-view--embedded) .ad-detail-draft-banner {
    width: calc(100% + 16px);
    max-width: calc(100% + 16px);
    margin-left: -8px;
    margin-right: -8px;
  }
}

.ad-detail-draft-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 12px 32px;
  background: #ffd02c;
  margin: 0;
}

.ad-detail-draft-banner__icon {
  flex-shrink: 0;
  width: 16px;
  height: 20px;
  display: block;
}

.ad-detail-draft-banner__content {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ad-detail-draft-banner__title {
  font-size: 16px !important;
  font-weight: 700;
  line-height: 1.3;
  color: #5c4300;
}

.ad-detail-draft-banner__desc {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #5c4300;
}

.ad-prior-year-reuse-banner {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 16px;
  border-radius: 8px;
  background: #f1e7ff;
  width: 100%;
  box-sizing: border-box;
}

.ad-prior-year-reuse-banner__content {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ad-prior-year-reuse-banner__title-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ad-prior-year-reuse-banner__icon {
  font-size: 20px;
  line-height: 1;
  color: #7128e8;
}

.ad-prior-year-reuse-banner__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: #7128e8;
}

.ad-prior-year-reuse-banner__desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
}

.ad-prior-year-reuse-banner__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.ad-prior-year-reuse-banner__link {
  border: none;
  background: transparent;
  padding: 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: #7128e8;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.ad-detail-overlay {
  display: block;
}

::v-deep .ad-detail-overlay > .b-overlay-wrap {
  min-height: inherit;
}

.ad-detail-overlay {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  align-self: stretch;
  box-sizing: border-box;
  /* 不要设 z-index：否则会形成层叠上下文，把「样本」b-sidebar(1000) 压在吸顶表头(10) 下面，右边框被盖住 */
  position: relative;
}

.ad-detail-overlay ::v-deep > .b-overlay-wrap {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100% !important;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.ad-detail-card {
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  align-self: stretch;
}

.ad-detail-card--compact {
  width: 100%;
  max-width: 100%;
  flex: 1 1 auto;
}

.ad-detail-view--embedded .ad-detail-card,
.ad-detail-view--embedded .ad-detail-card--compact {
  min-height: 100%;
}

.ad-detail-card--compact.ad-detail-card--loading {
  min-width: 0;
}

.ad-detail-overlay--compact {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.ad-detail-overlay--compact ::v-deep > .b-overlay-wrap {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.ad-detail-overlay--compact ::v-deep > .b-overlay-wrap:has(.ad-detail-card--loading) {
  min-width: 0;
}

.ad-detail-card--compact ::v-deep .ad-detail-mode-body,
.ad-detail-card--compact ::v-deep .ad-record-group-panel {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex: 1 1 auto;
}

.ad-detail-card--compact ::v-deep .ad-detail-mode-body .Ad-Top-Product {
  overflow: visible;
}

.ad-detail-card--compact ::v-deep .adPageCard.adPageCard--detail {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  align-items: stretch;
}

.ad-detail-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* 横幅广告左右并排时允许卡片内横向滚动；带产品选择的 compact 页不能加 overflow，否则会裁切下拉 */
.ad-detail-card:not(.ad-detail-card--compact) > .ad-detail-card__body {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.ad-detail-card--loading .ad-detail-card__body {
  min-height: 480px;
}

::v-deep .ad-detail-card__body .ad-info-center-core {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.ad-detail-card--loading ::v-deep .ad-detail-card__body .ad-info-center-core {
  min-height: 480px;
}

::v-deep .ad-detail-card__body .ad-detail-mode-body {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.ad-detail-card--loading ::v-deep .ad-detail-card__body .ad-detail-mode-body {
  min-height: 480px;
}

.ad-detail-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  column-gap: 16px;
  row-gap: 0;
  width: 100%;
  min-height: 40px;
  box-sizing: border-box;
  /* 与产品审批 TabHeader_admin 一致：吸顶；间距画在底色内，避免透出左右栏竖线 */
  position: sticky !important;
  top: 0;
  z-index: 10;
  background: #fff;
  background-clip: padding-box;
  border-bottom: 1px solid transparent;
  padding-bottom: 16px;
  pointer-events: auto;
}

.ad-detail-view:not(.ad-detail-view--embedded) .ad-detail-toolbar {
  background: transparent;
}

.ad-detail-toolbar + * {
  margin-top: 0;
}

.ad-detail-toolbar__main {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.ad-detail-status-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.ad-detail-status-label {
  font-size: 13px;
  font-weight: 400;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.6);
}

.ad-detail-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  box-sizing: border-box;
  height: 26px;
  padding: 0 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 10px;
  white-space: nowrap;
}

.ad-detail-launch-dot {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9e9e9e;
  flex-shrink: 0;
  font-style: normal;
}

.ad-detail-status-pill.is-launch-online {
  background: rgba(81, 187, 37, 0.18);
  color: #51bb25;
}

.ad-detail-status-pill.is-launch-online .ad-detail-launch-dot {
  background: #51bb25;
}

.ad-detail-status-pill.is-launch-offline {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.6);
}

.ad-detail-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 0;
}

.ad-detail-breadcrumb__link {
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font-family: inherit;
  font-size: 18px;
  font-weight: 400;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.60); /* 全部广告 */
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease;

  &:hover {
    color: rgba(0, 0, 0, 0.8);
  }
}

.iconRight {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  font-size: 20px;
  color: rgba(0, 0, 0, 0.60);
  user-select: none;
}

.ad-detail-breadcrumb__segment {
  font-size: 18px;
  font-weight: 600; /* 横幅广告等类型名 */
  line-height: 24px;
  color: rgba(0, 0, 0, 0.8);
  white-space: nowrap;
}

.ad-detail-breadcrumb__dash {
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.8);
  user-select: none;
}

.ad-detail-breadcrumb__current {
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.8);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.ad-detail-breadcrumb__id {
  font-weight: 700;
  white-space: nowrap;
}

.ad-detail-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.ad-detail-toolbar-last-submit {
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
}

.ad-detail-toolbar-status {
  margin-left: 8px;
}

.ad-detail-nav-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ad-detail-nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  padding: 8px 16px;
  border: none;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: rgba(0, 0, 0, 0.60);
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: #faf7ff;
    box-shadow: 0 0 12px rgba(115, 102, 255, 0.14);
    color: #7366ff;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.ad-detail-nav-btn__icon {
  font-size: 14px;
  font-weight: bold;
  color: inherit;
}

.ad-detail-bottom-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  /* 低于 TipsAndPrivewDilog 的 b-sidebar-outer(1000)，避免预览打开时按钮叠在预览上 */
  z-index: 999;
  display: flex;
  align-items: stretch;
  min-height: 62px;
  padding: 11px 10px;
  background: #fff;
  border-radius: 8px 0 0 0;
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.16);
  box-sizing: border-box;
}

.ad-detail-bottom-bar__save,
.ad-detail-bottom-bar__submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 40px;
  padding: 8px 24px;
  border: none;
  font-size: 16px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: #fff;
  cursor: pointer;
  transition: opacity 0.15s ease, filter 0.15s ease;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.ad-detail-bottom-bar__save {
  background: #7366ff;
  border-radius: 4px 0 0 4px;

  &:hover:not(:disabled) {
    filter: brightness(0.95);
  }
}

.ad-detail-bottom-bar__submit {
  background: #51bb25;
  border-radius: 0 4px 4px 0;

  &:hover:not(:disabled) {
    filter: brightness(0.95);
  }
}

.ad-detail-bottom-bar__sync {
  background: #ffc107;
  color: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.8);
  border-radius: 4px;
  margin-left: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 40px;
  padding: 8px 24px;
  font-size: 16px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: opacity 0.15s ease, filter 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(0.95);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.ad-detail-card-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
}

.ad-detail-card-header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  min-height: 26px;
}

.ad-detail-card-header__title {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: #000;
}

.ad-detail-display-in {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
}

.ad-detail-display-in__label {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.8);
}

.ad-detail-platform-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.ad-detail-platform-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.ad-detail-platform-logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f9f9f9;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.ad-detail-platform-name {
  min-width: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
}

.ad-detail-open-display {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  outline: none;
}

.ad-detail-open-display .tips-pop {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  left: auto;
  transform: translateY(-2px);
  opacity: 0;
  pointer-events: none;
  z-index: 3000;
  transition: opacity 0.12s ease, transform 0.12s ease;
}

.ad-detail-open-display:hover .tips-pop,
.ad-detail-open-display:focus .tips-pop,
.ad-detail-open-display:focus-within .tips-pop {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.ad-detail-open-display .tips-box {
  position: relative;
  max-width: 320px;
  min-width: 220px;
  padding: 10px 14px;
  border-radius: 8px;
  background: linear-gradient(180deg, #6b50f0 0%, #5a46e6 100%);
  color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
  font-size: 13px;
  line-height: 19px;
  font-weight: 600;
  text-align: left;
  white-space: normal;
  word-break: break-word;
}

.ad-detail-open-display .tips-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #fff;
}

.ad-detail-open-display .tips-arrow {
  position: absolute;
  top: -6px;
  right: 24px;
  left: auto;
  width: 12px;
  height: 12px;
  background: #6b50f0;
  transform: rotate(45deg);
  border-radius: 2px;
}

.ad-detail-reject-banner {
  padding: 16px;
  border-radius: 8px;
  background: #fcebed;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
}

.ad-detail-reject-banner__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ad-detail-reject-banner__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
}

.ad-detail-reject-banner__reason {
  margin: 0;
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
}

::v-deep .ad-detail-mode-body {
  width: 100%;
  max-width: 100%;
  min-width: 0;

  .ad-record-group-panel {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .adPageCard.adPageCard--detail {
    border: none;
    box-shadow: none;
    padding: 0;
    gap: 16px;
    align-items: stretch;
    margin-top: 0;
    width: 100%;
    min-width: 100%;
    flex-wrap: nowrap;
    box-sizing: border-box;
  }

  /* 参展商名单宽 logo：左右栏随内容撑开，由页面级横向滚动 */
  .adPageCard.adPageCard--detail.adPageCard--logo-wide {
    width: max-content;
    min-width: 100%;
  }

  .ad-detail-mode-body:has(.adPageCard--logo-wide) .ad-record-group-panel {
    width: max-content;
    min-width: 100%;
    max-width: none;
  }

  .adPageCard.adPageCard--detail.adPageCard--logo-wide > .adPreviewSection {
    flex: 0 0 auto;
    width: auto;
    min-width: max-content;
    overflow: visible;
  }

  .adPageCard.adPageCard--detail.adPageCard--logo-wide .adPreviewCardWrapper--detail {
    overflow: visible;
    width: max-content;
    min-width: 100%;
    flex: 0 0 auto;
  }

  .adPageCard.adPageCard--detail.adPageCard--logo-wide .PrivewDialogBody,
  .adPageCard.adPageCard--detail.adPageCard--logo-wide .AdBlock.PrivewDialog {
    overflow: visible;
  }

  .adPageCard.adPageCard--detail > .adDetailCardWrapper {
    width: 591px;
    min-width: 591px;
    max-width: 591px;
    flex: 0 0 591px;
    box-sizing: border-box;
  }

  .adPreviewCardWrapper.adPreviewCardWrapper--detail {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 8px;
    padding: 16px;
    /* 高度 hug：只随预览内容，不跟左侧配置等高 */
    align-self: flex-start;
    height: fit-content;
    box-sizing: border-box;
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  /* 横幅：左右布局，预览区保持最小宽度并拉满右侧宽度；高度仍 hug */
  .adPageCard.adPageCard--detail.adPageCard-Wh .adPreviewCardWrapper.adPreviewCardWrapper--detail {
    min-width: 500px;
    flex: 1 1 0;
    align-self: flex-start;
    height: fit-content;
  }

  /* 非横幅：预览区拉满右侧剩余宽度；高度 hug */
  .adPageCard.adPageCard--detail:not(.adPageCard-Wh) .adPreviewCardWrapper.adPreviewCardWrapper--detail {
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    align-self: flex-start;
    height: fit-content;
  }

  .adPreviewSection.adPreviewSection--detail {
    align-self: flex-start;
    height: fit-content;
  }

  .ad-preview-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 24px;
    padding: 0;
    margin: 0 0 12px;
    box-sizing: border-box;
  }

  .ad-preview-panel-heading {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
  }

  .ad-preview-panel-title {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.198px;
    color: rgba(0, 0, 0, 0.8);
  }

  .ad-preview-bilingual__badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.05);
    color: rgba(0, 0, 0, 0.8);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
  }

  .privewBtn {
    padding: 4px 8px !important;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    /* Figma 1464:24601 View Sample hover：Surface Hover 3% + Outline 15% */
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.03) !important;
      border-color: rgba(0, 0, 0, 0.15) !important;
      color: rgba(0, 0, 0, 0.6) !important;
    }
  }

  .adPageCard.adPageCard--detail:not(.adPageCard-Wh) .adPreviewCardWrapper--detail .AdBlock.PrivewDialog,
  .adPageCard.adPageCard--detail:not(.adPageCard-Wh) .adPreviewCardWrapper--detail .PrivewDialogBody {
    width: 100%;
    max-width: 100%;
  }

  .adPreviewCardWrapper--detail .AdBlock.PrivewDialog {
    padding: 0;
    background: transparent;
    border-radius: 0;
    min-width: 0;
    width: 100%;
    flex: 1 1 auto;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }

  .adPageCard.adPageCard--detail.adPageCard-Wh .adPreviewCardWrapper--detail .AdBlock.PrivewDialog,
  .adPageCard.adPageCard--detail.adPageCard-Wh .adPreviewCardWrapper--detail .PrivewDialogBody {
    width: 100%;
  }

  .adPreviewCardWrapper--detail .PrivewDialogBody {
    box-sizing: border-box;
    width: 100%;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }

  /* 其它 logo 变体（首页/圆形等）仍可在预览区内横向滚动 */
  .adPageCard.adPageCard--detail:not(.adPageCard--logo-wide) .PrivewDialogBody:has(.logoV2),
  .adPageCard.adPageCard--detail:not(.adPageCard--logo-wide) .PrivewDialogBody:has(.logo-block):not(:has(.ad-card)) {
    overflow-x: auto;
    overflow-y: hidden;
    align-items: stretch;
    width: 100%;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
  }

  /* 横幅预览仍铺满宽度 */
  .adPageCard.adPageCard--detail.adPageCard-Wh .adPreviewCardWrapper--detail .PrivewDialogBody {
    align-items: stretch;
  }

  /* 非横幅：预览内容在右侧区域水平居中（参展商名单宽 logo 由页面级滚动） */
  .adPageCard.adPageCard--detail:not(.adPageCard-Wh):not(.adPageCard--logo-wide) .PrivewDialogBody > div {
    margin-left: auto;
    margin-right: auto;
    width: fit-content;
    max-width: 100%;
  }

  .adPageCard.adPageCard--detail:not(.adPageCard-Wh) .PrivewDialogBody:has(.logoV2) > div,
  .adPageCard.adPageCard--detail:not(.adPageCard-Wh) .PrivewDialogBody:has(.logo-block):not(:has(.ad-card)) > div {
    max-width: none;
    width: max-content;
    min-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  .adPageCard.adPageCard--detail:not(.adPageCard-Wh) .adPreviewCardWrapper--detail .PrivewDialogBody .productCard {
    margin-left: auto;
    margin-right: auto;
  }

  .adPreviewCardWrapper--detail .AdBlock:has(.btnad) .btnad__list {
    padding: 12px 8px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.65);
  }

  .adDetailCardWrapper .ad-title-row {
    min-height: 24px;
  }
}

@media (max-width: 1200px) {
  .ad-detail-toolbar {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .ad-detail-toolbar__actions {
    width: 100%;
    justify-content: space-between;
    justify-self: end;
  }

  .ad-prior-year-reuse-banner {
    flex-wrap: wrap;
  }
}

@media (max-width: 992px) {
  .ad-detail-breadcrumb {
    flex-wrap: wrap;
    row-gap: 6px;
  }

  .ad-detail-toolbar-status {
    margin-left: 0;
  }

  .ad-detail-toolbar__actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}
</style>
